import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment } from "@angular/router";
import { NavigationService, NotificationsService, NotificationTypeEnum } from '../modules/tcm-core.module';
import { Observable } from "rxjs";
import { ReturnUrlInfo } from '../internals/return-url-info';
import { PermissionsService } from "../services";
import { AuthService } from "../services/auth.service";
import { LoginComponentService } from '../services/system/login-component.service';
import { TranslationService } from "../services/translation.service";
import { Location } from "@angular/common";

@Injectable({ providedIn: 'root' })
export class AuthPermissionsGuard  {

    constructor(
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly loginComponentService: LoginComponentService,
        private permissionService: PermissionsService,
        private translationService: TranslationService,
        private notificationService: NotificationsService,
        private location: Location) {
    }
    canLoad(route: Route, segments: UrlSegment[]): boolean | Observable<boolean> | Promise<boolean> {
        if (!this.checkCommonRequirements(route)) {
            return false;
        }

        if (!route.data.areaPermissionKey) {
            return route.data.ignorePermissionsCheck;
        }

        const hasAccess = this.permissionService.checkAreaAccess(route.data.areaPermissionKey);

        if (!hasAccess) {
            this.showAuthorizationError();
        }

        return hasAccess;
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
        if (!this.checkCommonRequirements(route)) {
            return false;
        }

        if (!route.data.areaPermissionKey) {
            return route.data.ignorePermissionsCheck;
        }

        const hasAccess = this.permissionService.checkAreaAccess(route.data.areaPermissionKey);

        if (!hasAccess) {
            this.showAuthorizationError();
        }

        return hasAccess;
    }
    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
        if (!childRoute.component)
            return true;

        if (this.checkCommonRequirements(childRoute)) {
            if (childRoute.data.ignorePermissionsCheck === true)
                return true;

            if (childRoute.data.ignoreEntityPermissionsCheck) {
                if(this.permissionService.checkAreaAccess(childRoute.data.areaPermissionKey))
                    return true;
            }
            else if (childRoute.data.entityPermissionKey && this.permissionService.checkEntityAccess(childRoute.data.areaPermissionKey, childRoute.data.entityPermissionKey)) {
                return true;
            }
        }

        this.showAuthorizationError();

        return false;
    }

    private checkCommonRequirements(route: Route | ActivatedRouteSnapshot) {
        
        const currentUser = this.authService.currentUserValue;

        if (!currentUser) {

            const returnUrlInfo = this.getReturnUrl();

            this.loginComponentService.navigateToLogin(returnUrlInfo);

            return false;
        }

        if (!route.data)
            return false;

        if (!currentUser.defaultClinicId && route.data.ignoreDefaultClinicCheck !== true) {

            const returnUrlInfo = this.getReturnUrl();

            this.router.navigate(['/user-default-info'], { queryParams: returnUrlInfo });

            return false;
        }

        if (currentUser.defaultClinicId && currentUser.isEnforcingEndOfDay && !this.getLocation().includes('/sales-orders/end-of-days')) {

            this.router.navigate(['/sales-orders/end-of-days']);

            return false;
        }

        return true;
    }

    private getReturnUrl(): ReturnUrlInfo {
        const currentNavigation = this.router.getCurrentNavigation();

        if (currentNavigation == null)
            return null;

        const url = this.router.serializeUrl(currentNavigation.extractedUrl)

        return {
            returnUrl: url
        };
    }

    private getLocation() {
        const current = this.router.getCurrentNavigation();

        if (current == null)
            return null;

        const url = this.router.serializeUrl(current.extractedUrl);

        return url;
    }

    private showAuthorizationError() {
        
        const errorMessage = this.translationService.translate(x => x.Messages.ErrorMessages.ScreenAccessDenied);

        this.notificationService.add({
            type: NotificationTypeEnum.Error,
            message: errorMessage
        });
    }
}